import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/ui/date-picker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useExpenseCategories } from '@/hooks/use-expense-categories';
import { useCreateExpense, useUpdateExpense, useUploadAttachment, Expense } from '@/hooks/use-expenses';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/use-auth';
import { useNavigate, useParams } from 'react-router-dom';
import { Upload, X, FileText, Settings } from 'lucide-react';
import { formatCurrency, validateFile } from '@/lib/expense-utils';
import { ExpenseStatusBadge } from './ExpenseStatusBadge';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { DynamicIcon } from '@/components/ui/dynamic-icon';

const expenseSchema = z.object({
  category_id: z.string().min(1, 'Categoria é obrigatória'),
  description: z.string().max(500, 'Máximo 500 caracteres'),
  vehicle_id: z.string().min(1, 'Veículo é obrigatório'),
  driver_id: z.string().optional(),
  payment_type: z.enum(['single', 'installment', 'recurring']),
  installment_total: z.number().min(2).max(60).optional(),
  recurrence_interval: z.enum(['monthly', 'quarterly', 'yearly']).optional(),
  due_date: z.string().min(1, 'Data de vencimento é obrigatória'),
  value: z.number().min(0.01, 'Valor deve ser maior que zero'),
  paid_at: z.string().optional(),
  notes: z.string().optional(),
});

type ExpenseFormData = z.infer<typeof expenseSchema>;

interface ExpenseFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  expense?: Expense;
}

export function ExpenseFormSheet({ open, onOpenChange, expense }: ExpenseFormSheetProps) {
  const { activeCompany } = useAuth();
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const [file, setFile] = useState<File | null>(null);
  const [markAsPaid, setMarkAsPaid] = useState(false);
  const [paymentType, setPaymentType] = useState<'single' | 'installment' | 'recurring'>('single');

  const { data: categories } = useExpenseCategories();
  const { data: vehicles } = useQuery({
    queryKey: ['vehicles', activeCompany?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('fleet_vehicles')
        .select('id, plate, model')
        .eq('company_id', activeCompany?.id)
        .eq('status', 'active')
        .order('plate');
      return data || [];
    },
    enabled: !!activeCompany?.id,
  });

  const { data: drivers } = useQuery({
    queryKey: ['drivers', activeCompany?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('fleet_drivers')
        .select('id, name')
        .eq('company_id', activeCompany?.id)
        .eq('status', 'active')
        .order('name');
      return data || [];
    },
    enabled: !!activeCompany?.id,
  });

  const createExpense = useCreateExpense();
  const updateExpense = useUpdateExpense();
  const uploadAttachment = useUploadAttachment();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<ExpenseFormData>({
    resolver: zodResolver(expenseSchema),
    defaultValues: {
      payment_type: 'single',
      value: 0,
    },
  });

  const watchedValues = watch();

  useEffect(() => {
    if (expense) {
      reset({
        category_id: expense.category_id,
        description: expense.description,
        vehicle_id: expense.vehicle_id || '',
        driver_id: expense.driver_id || '',
        payment_type: expense.payment_type,
        installment_total: expense.installment_total || undefined,
        recurrence_interval: expense.recurrence_interval || undefined,
        due_date: expense.due_date,
        value: Number(expense.value),
        paid_at: expense.paid_at || undefined,
        notes: expense.notes || undefined,
      });
      setPaymentType(expense.payment_type);
      setMarkAsPaid(!!expense.paid_at);
    }
  }, [expense, reset]);

  const onSubmit = async (data: ExpenseFormData) => {
    try {
      const expenseData: any = {
        ...data,
        paid_at: markAsPaid ? data.paid_at : null,
        driver_id: data.driver_id || null,
        notes: data.notes || null,
      };

      if (paymentType === 'single') {
        expenseData.installment_number = null;
        expenseData.installment_total = null;
        expenseData.recurrence_interval = null;
      } else if (paymentType === 'installment') {
        expenseData.recurrence_interval = null;
      } else if (paymentType === 'recurring') {
        expenseData.installment_number = null;
        expenseData.installment_total = null;
      }

      let newExpense;
      if (expense) {
        await updateExpense.mutateAsync({ id: expense.id, ...expenseData });
        newExpense = expense;
      } else {
        newExpense = await createExpense.mutateAsync(expenseData);
      }

      if (file && newExpense) {
        const { url, name } = await uploadAttachment.mutateAsync({
          file,
          expenseId: newExpense.id,
        });
        await updateExpense.mutateAsync({
          id: newExpense.id,
          attachment_url: url,
          attachment_name: name,
        });
      }

      onOpenChange(false);
      reset();
      setFile(null);
      setMarkAsPaid(false);
      setPaymentType('single');
    } catch (error) {
      console.error('Error saving expense:', error);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      const validation = validateFile(selectedFile);
      if (validation.valid) {
        setFile(selectedFile);
      } else {
        alert(validation.error);
      }
    }
  };

  const selectedCategory = categories?.find((c) => c.id === watchedValues.category_id);
  const selectedVehicle = vehicles?.find((v) => v.id === watchedValues.vehicle_id);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:w-[650px] p-6 overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{expense ? 'Editar Despesa' : 'Nova Despesa'}</SheetTitle>
          <SheetDescription>
            Preencha os dados da despesa relacionada ao veículo
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-6">
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2 space-y-6">
              <div className="space-y-2">
                <Label htmlFor="category_id">Categoria *</Label>
                <Select
                  value={watchedValues.category_id}
                  onValueChange={(value) => setValue('category_id', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories?.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        <div className="flex items-center gap-2">
                          <div style={{ color: category.color }}>
                            <DynamicIcon name={category.icon} size={16} />
                          </div>
                          <span>{category.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.category_id && (
                  <p className="text-sm text-destructive">{errors.category_id.message}</p>
                )}
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-xs"
                  onClick={() => navigate(`/company/${slug}/admin/systems/fleet/expense-categories`)}
                >
                  <Settings className="w-3 h-3 mr-1" />
                  Gerenciar categorias
                </Button>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  placeholder="Qual o motivo da despesa?"
                  maxLength={500}
                  {...register('description')}
                />
                {errors.description && (
                  <p className="text-sm text-destructive">{errors.description.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="vehicle_id">Veículo *</Label>
                <Select
                  value={watchedValues.vehicle_id}
                  onValueChange={(value) => setValue('vehicle_id', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o veículo" />
                  </SelectTrigger>
                  <SelectContent>
                    {vehicles?.map((vehicle) => (
                      <SelectItem key={vehicle.id} value={vehicle.id}>
                        {vehicle.plate} - {vehicle.model}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.vehicle_id && (
                  <p className="text-sm text-destructive">{errors.vehicle_id.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="driver_id">Motorista</Label>
                <Select
                  value={watchedValues.driver_id}
                  onValueChange={(value) => setValue('driver_id', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o motorista (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {drivers?.map((driver) => (
                      <SelectItem key={driver.id} value={driver.id}>
                        {driver.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Anexar arquivo</Label>
                <div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                  <input
                    type="file"
                    id="file-upload"
                    className="hidden"
                    accept=".png,.jpg,.jpeg,.pdf,.mp4,.xlsx,.xls,.csv"
                    onChange={handleFileChange}
                  />
                  {file ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        <span className="text-sm">{file.name}</span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setFile(null)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        Arraste e solte ou clique para selecionar
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        PNG, JPEG, PDF, MP4, XLSX, XLS, CSV até 20MB
                      </p>
                    </label>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Pagamento</Label>
                <Tabs
                  value={paymentType}
                  onValueChange={(value: any) => {
                    setPaymentType(value);
                    setValue('payment_type', value);
                  }}
                >
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="single">Único</TabsTrigger>
                    <TabsTrigger value="installment">Parcelado</TabsTrigger>
                    <TabsTrigger value="recurring">Recorrente</TabsTrigger>
                  </TabsList>
                </Tabs>

                {paymentType === 'installment' && (
                  <div className="mt-4 space-y-2">
                    <Label htmlFor="installment_total">Número de parcelas</Label>
                    <Input
                      id="installment_total"
                      type="number"
                      min="2"
                      max="60"
                      {...register('installment_total', { valueAsNumber: true })}
                    />
                  </div>
                )}

                {paymentType === 'recurring' && (
                  <div className="mt-4 space-y-2">
                    <Label htmlFor="recurrence_interval">Intervalo</Label>
                    <Select
                      value={watchedValues.recurrence_interval}
                      onValueChange={(value: any) => setValue('recurrence_interval', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o intervalo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Mensal</SelectItem>
                        <SelectItem value="quarterly">Trimestral</SelectItem>
                        <SelectItem value="yearly">Anual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="due_date">Data de vencimento *</Label>
                <DatePicker
                  value={watchedValues.due_date}
                  onChange={(date) => setValue('due_date', date || '')}
                />
                {errors.due_date && (
                  <p className="text-sm text-destructive">{errors.due_date.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="value">Valor *</Label>
                <Input
                  id="value"
                  type="number"
                  step="0.01"
                  min="0.01"
                  {...register('value', { valueAsNumber: true })}
                />
                {errors.value && (
                  <p className="text-sm text-destructive">{errors.value.message}</p>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="mark-paid"
                  checked={markAsPaid}
                  onCheckedChange={(checked) => {
                    setMarkAsPaid(checked);
                    if (checked && !watchedValues.paid_at) {
                      setValue('paid_at', new Date().toISOString().split('T')[0]);
                    }
                  }}
                />
                <Label htmlFor="mark-paid">Marcar como paga</Label>
              </div>

              {markAsPaid && (
                <div className="space-y-2">
                  <Label htmlFor="paid_at">Data de pagamento</Label>
                  <DatePicker
                    value={watchedValues.paid_at}
                    onChange={(date) => setValue('paid_at', date || '')}
                  />
                </div>
              )}
            </div>

            <div className="col-span-1">
              <div className="sticky top-6 space-y-4 p-4 border rounded-lg bg-muted/30">
                <h4 className="font-semibold text-sm">Detalhes da despesa</h4>
                <div className="space-y-3 text-sm">
                  <div>
                    <p className="text-muted-foreground">Categoria</p>
                    <p className="font-medium">{selectedCategory?.name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Veículo</p>
                    <p className="font-medium">{selectedVehicle?.plate || '-'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Tipo</p>
                    <p className="font-medium">
                      {paymentType === 'single' && 'Único'}
                      {paymentType === 'installment' && 'Parcelado'}
                      {paymentType === 'recurring' && 'Recorrente'}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Vencimento</p>
                    <p className="font-medium">
                      {watchedValues.due_date
                        ? format(new Date(watchedValues.due_date), 'dd/MM/yyyy', { locale: ptBR })
                        : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Valor</p>
                    <p className="font-medium text-lg">
                      {formatCurrency(watchedValues.value || 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground mb-1">Status</p>
                    <ExpenseStatusBadge
                      status={markAsPaid ? 'paid' : 'pending'}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-6 border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={createExpense.isPending || updateExpense.isPending}>
              {expense ? 'Salvar' : 'Criar despesa'}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
